import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  Pipe,
  PipeTransform,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from "@angular/forms";
import { NgSelectModule } from "@ng-select/ng-select";
import { from } from 'rxjs/internal/observable/from';
import { of } from 'rxjs/internal/observable/of';
import { Subject } from 'rxjs/internal/Subject';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { WideDropdownDirective } from '../../directives/wide-dropdown.directive';
import { STORAGE_TOKEN, UILocalStorageService, UIStorageService } from '../../services/storage.service';
import { FilterPillComponent } from "../filter-pill/filter-pill.component";
import { SearchComponent } from "../search/search";

export interface IDictionaryItem {
  id: number;
  value: string;
  publish?: boolean;
  data?: { [key: string]: any };
}

export type IDictionaryResponse = { [key: string]: IDictionaryItem[] };
export type FilterItemType = 'checkbox' | 'text' | 'date' | 'toggle-buttons';

export interface IFilterItem {
  id: string | number;
  value: string;
  data?: {[key: string]: any};
}

export interface IFilterDesc {
  id: string; // Id of filter
  label?: string; // Label of filter
  items?: IFilterItem[]; // Filter items
  value?: any; // Current filter value
  parameter?: string; // Url parameter name, by default used id
  dropdownSearch?: boolean; // Enable search in ng-select
  separatePills?: boolean; // For multiple mode, show selected values on separate pills
  type?: FilterItemType;
  multiple?: boolean;
  appendTo?: string; // Element to which the dropdown should be appended, i.e. 'body'
  wide?: boolean; // Widen the dropdown to fit the longest option, implies appendTo 'body'
  hidden?: boolean;
  newLine?: boolean;
  width?: string;
  class?: string; // Extra css classes for the dropdown, i.e. 'ellipsis-tags' to truncate long selected values
  getFilterValue?: (v: any) => string;
  // Loads dropdown items from the server as the user types. Such a filter keeps whole
  // IFilterItem objects as its value, so labels survive a reload without a lookup-by-id endpoint.
  search?: (term: string) => Promise<IFilterItem[]>;
}

export type IFilter = IFilterDesc[];

const SEARCH_DEBOUNCE = 300;
const NO_ITEMS: IFilterItem[] = [];

export interface IStoredFilter {
  id: string;
  value: any;
}

@Pipe({
  name: 'filterHidden',
  standalone: true
})
export class FilterHiddenPipe implements PipeTransform {
  transform(items: any[]): any[] {
    if (!items) return [];
    return items.filter(item => !item.hidden);
  }
}

@Component({
  selector: 'ui-filter',
  imports: [
    SearchComponent,
    NgSelectModule,
    FormsModule,
    FilterPillComponent,
    FilterHiddenPipe,
    WideDropdownDirective
  ],
  templateUrl: './filter.component.html',
  styleUrl: './filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.as-popup]': 'showAsPopup()',
    '[class.multiline]': 'multiline()'
  }
})
export class FilterComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private injector = inject(Injector);
  private destroyRef = inject(DestroyRef);

  readonly isSearch = input(true);
  readonly showAsPopup = input(false);
  readonly wrap = input(false);
  readonly items = input<IFilter>([]);
  readonly storeFilters = input(false);
  readonly storeKey = input('');
  readonly multiline = input(false);
  readonly showFilters = input(true);
  readonly searchPlaceholder = input('');

  readonly onSearch = output<string>();
  readonly onChange = output<IFilterDesc|undefined>();
  readonly term = model('');

  protected readonly activeFilters = signal<IFilterDesc[]>([]);
  protected readonly loadingIds = signal<ReadonlySet<string>>(new Set());
  // Items fetched by `search` live here and not on the filter descriptor: a page reload rebuilds
  // the descriptors, and results written onto a replaced one would never reach the dropdown
  protected readonly searchItems = signal<Record<string, IFilterItem[]>>({});
  private readonly typeaheads = new Map<string, Subject<string>>();
  private readonly fxUpdateActiveFilters = effect(() => {
    this.items();
    this.updateActiveFilters();
  });

  protected readonly hasFilters = computed(() =>
    this.activeFilters().length > 0 || this.term().length > 0
  );

  protected readonly hasNewLineFilters = computed(() =>
    this.items().some(f => !!f.newLine && !f.hidden)
  );

  protected readonly filterRows = computed(() => {
    const rows: IFilterDesc[][] = [[]];
    this.items()
      .filter(f => !f.hidden)
      .forEach(flt => {
        if (flt.newLine && rows[rows.length - 1].length) {
          rows.push([]);
        }
        rows[rows.length - 1].push(flt);
      });

    return rows.filter(r => r.length);
  });

  private readonly storage: UIStorageService;

  constructor() {
    const storage = inject<UIStorageService>(STORAGE_TOKEN, { optional: true });
    this.storage = storage || this.injector.get(UILocalStorageService);
  }

  static fillFiltersFromResponse(filter: IFilter | undefined, resp: IDictionaryResponse) {
    if (!filter) {
      return;
    }
    Object.keys(resp).forEach(key => {
      if (!resp[key]) {
        return;
      }
      const flt = filter.find(f => f.id === key);
      if (flt) {
        flt.items = resp[key] as unknown as IFilterItem[];
      }
    });
  }


  static getFilter(filter: IFilter | undefined, id: string) {
    if (!filter) {
      return;
    }
    return filter.find(f => f.id === id);
  }

  static setFilter(filter: IFilter | undefined, id: string, items?: IFilterItem[], resetValue = false) {
    if (!filter) {
      return;
    }
    const flt = filter.find(f => f.id === id);
    if (!flt || !items) {
      return;
    }
    if (resetValue) {
      flt.value = undefined;
    }
    flt.items = [...items];
  }

  static getFilterParams(filter?: IFilter, prefix = '') {
    if (!filter) {
      return '';
    }
    const params = filter
      .filter(f => f.value !== undefined && !f.hidden)
      .map(f => {
        let v = f.value;
        if (f.getFilterValue) {
          v = f.getFilterValue(v);
        } else if (f.search) {
          v = (Array.isArray(v) ? v : [v]).map((i: IFilterItem) => i?.id).join(',');
        } else if (f.type === 'checkbox') {
          v = f.value ? '1' : '0';
        }

        return encodeURIComponent(`${prefix}${f.parameter || f.id}`) + '=' + encodeURIComponent(`${v}`)
      })
      .join('&');

    return params ? '&' + params : '';
  }

  static loadFilterValuesFromStorage(filter: IFilter | undefined, storage: UIStorageService, storeKey: string) {
    if (!filter) {
      return;
    }
    const json = storage.get(storeKey);
    if (!json) {
      return;
    }
    try {
      const storedFilters: IStoredFilter[] = JSON.parse(json);
      FilterComponent.loadFilterValues(filter, storedFilters);
    } catch (e) {
      console.error(`Can't parse stored filters from JSON: `, json);
    }
  }

  static loadFilterValues(filter?: IFilter, storedFilters?: IStoredFilter[]) {
    if (!filter || !storedFilters) {
      return;
    }
    filter.forEach(flt => {
      const storedFilter = storedFilters.find(f => f.id === flt.id);
      if (storedFilter && flt.value === undefined) {
        flt.value = storedFilter.value;
      }
    });
  }

  static getStoredFilters(filters: IFilter) {
    return filters
      .filter(flt => flt.value !== undefined)
      .map(flt => ({id: flt.id, value: flt.value})) as IStoredFilter[];
  }

  trackFilter(flt: IFilterDesc): string {
    const ids = this.valueIds(flt);
    return `${flt.id}:${ids.join(',')}`;
  }

  // Async filters bind whole objects, so selected values must be matched by id and not by reference
  readonly compareItems = (a?: IFilterItem, b?: IFilterItem) => a?.id === b?.id;

  protected itemsOf(flt: IFilterDesc) {
    return (flt.search ? this.searchItems()[flt.id] : flt.items) ?? NO_ITEMS;
  }

  protected getTypeahead(flt: IFilterDesc) {
    if (!flt.search) {
      return undefined;
    }
    const id = flt.id;
    let typeahead = this.typeaheads.get(id);
    if (!typeahead) {
      typeahead = new Subject<string>();
      this.typeaheads.set(id, typeahead);
      typeahead.pipe(
        // ng-select pushes null when it clears its own input (e.g. on blur), despite the Subject being typed as string
        map(term => term ?? ''),
        debounceTime(SEARCH_DEBOUNCE),
        distinctUntilChanged(),
        tap(() => this.setLoading(id, true)),
        // Look the descriptor up by id on every term: reloading data replaces the object `flt` points at
        switchMap(term => from(this.findSearch(id)(term)).pipe(catchError(() => of(NO_ITEMS)))),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(items => {
        this.searchItems.update(all => ({...all, [id]: items}));
        this.setLoading(id, false);
        this.cdr.detectChanges();
      });
    }
    return typeahead;
  }

  protected isLoading(flt: IFilterDesc) {
    return this.loadingIds().has(flt.id);
  }

  // Show something as soon as the dropdown opens instead of an empty "type to search" panel
  protected onDropdownOpen(flt: IFilterDesc) {
    if (flt.search && !this.searchItems()[flt.id]?.length) {
      this.typeaheads.get(flt.id)?.next('');
    }
  }

  private findSearch(id: string) {
    const search = this.items().find(f => f.id === id)?.search;
    return search ?? (() => Promise.resolve(NO_ITEMS));
  }

  private setLoading(id: string, isLoading: boolean) {
    this.loadingIds.update(ids => {
      const next = new Set(ids);
      if (isLoading) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }

  private valueIds(flt: IFilterDesc): any[] {
    const v = flt.value;
    if (v === undefined) {
      return [];
    }
    const values = Array.isArray(v) ? v : [v];
    return values.map(i => (i && typeof i === 'object') ? i.id : i);
  }

  ngOnInit() {
    this.loadFilterValues();
  }

/*   ngOnChanges(changes: SimpleChanges) {
    if (changes['items'] && changes['items'].previousValue !== changes['items'].currentValue) {
      this.cdr.detectChanges();
    }
  } */

  onSearchHandler(term: string) {
    this.term.set(term);
    this.onSearch.emit(term);
  }

  onFilter(flt: IFilterDesc) {
    // Remove filter value is checkbox is unchecked
    if (flt && flt.type === 'checkbox') {
      if (!flt.value) {
        flt.value = undefined;
      }
    }

    // Remove empty arrays
    if (Array.isArray(flt.value) && flt.value.length === 0) {
      flt.value = undefined;
    }

    this.updateActiveFilters();
    this.saveFilterValues();
    this.onChange.emit(flt);
    // TODO: remove this when `hasFilters` computed will be solved to trigger after filter value change
    // this.cdr.detectChanges();
  }

  private updateActiveFilters() {
    // A toggle group shows its own state, so it needs no pill of its own and must not be what
    // makes the "Clear filters" row appear
    this.activeFilters.set(this.items().filter(f => f.value !== undefined && f.type !== 'toggle-buttons'));
  }

  selectToggle(flt: IFilterDesc, item: IFilterItem) {
    if (flt.value === item.id) {
      return;
    }
    flt.value = item.id;
    this.onFilter(flt);
  }

  getFilterValue(flt: IFilterDesc, value?: any) {
    if (flt.type === 'checkbox') {
      return flt.label /*+ ': ' + (flt.value === true ? 'Yes' : 'No')*/;
    }
    if (flt.type === 'text' || flt.type === 'date') {
      return flt.label + ': ' + flt.value;
    }
    // Async filters carry their own labels, there is nothing to look up in `items`
    if (flt.search) {
      const values: IFilterItem[] = value ? [value] : (flt.multiple ? (flt.value || []) : [flt.value]);
      return values.filter(v => !!v).map(v => v.value).join(', ');
    }
    let ids: any[] = [];
    if (flt.multiple) {
      ids = value ? [value] : (flt.value || []);
    } else {
      ids = [flt.value];
    }
    const items = flt.items?.filter(f => ids.includes(f.id)).map((f: IFilterItem) => f.value);
    return items?.length ? items.join(', ') : '';
  }

  onClear() {
    const hadTerm = !!this.term();
    // A toggle group picks between views rather than narrowing one, so it keeps its value
    this.items().forEach(flt => {
      if (flt.type !== 'toggle-buttons') {
        flt.value = undefined;
      }
    });
    this.activeFilters.set([]);
    this.saveFilterValues();

    // `term` is bound one way, so the consumer only learns of a cleared search through `onSearch`;
    // emitting `onChange` alone leaves it re-sending the old term. Both outputs would navigate
    // twice and leave two history entries, and either handler reads the filter values that were
    // just cleared above, so only one of them is emitted.
    if (hadTerm) {
      this.onSearchHandler('');
    } else {
      this.onChange.emit(undefined);
    }
  }

  removeFilter(flt: IFilterDesc) {
    flt.value = undefined;
    this.activeFilters.set(this.activeFilters().filter(f => f.id !== flt.id));
    this.saveFilterValues();
    this.onChange.emit(flt);
  }

  removeFilterValue(flt: IFilterDesc, v: any) {
    const idx = flt.value?.indexOf(v) ?? -1;
    if (idx !== -1) {
      flt.value.splice(idx, 1);
      if (flt.value.length === 0) {
        flt.value = undefined;
      } else {
        flt.value = [...flt.value];
      }
      this.updateActiveFilters();
      this.saveFilterValues();
      this.onChange.emit(flt);
    }
  }

  update() {
    this.cdr.detectChanges();
  }

  private saveFilterValues() {
    if (!this.storeFilters()) {
      return;
    }
    const storeKey = this.storeKey();
    if (!storeKey) {
      console.warn('You use [storeFilters]="true" on filter component, but [storeKey] was not specified');
      return;
    }
    const filters = FilterComponent.getStoredFilters(this.items());
    if (filters.length) {
      this.storage.set(storeKey, JSON.stringify(filters));
    } else {
      this.storage.remove(storeKey);
    }
  }

  private loadFilterValues() {
    if (!this.storeFilters()) {
      return;
    }
    const storeKey = this.storeKey();
    if (!storeKey) {
      console.warn('You use [storeFilters]="true" on filter component, but [storeKey] was not specified');
      return;
    }
    FilterComponent.loadFilterValuesFromStorage(this.items(), this.storage, storeKey);
    this.updateActiveFilters();
  }
}